import { Injectable, inject } from '@angular/core';

import { AppConfigService } from '../config/app-config.service';
import { PasswordPolicyError } from './password-recovery.errors';
import {
  InvalidTenantInvitationError,
  TooManyInvitationAttemptsError,
} from './tenant-invitation.errors';

/**
 * Resultado de GET /tenant-invitations/validate. El endpoint SIEMPRE responde
 * 200: `{ valida: true, nombreTenant, email }` o `{ valida: false }` — opaco a
 * propósito (nunca distingue expirada / usada / inexistente).
 */
export type TenantInvitationValidation =
  | { readonly valida: true; readonly nombreTenant: string; readonly email: string }
  | { readonly valida: false };

/**
 * Códigos de la política de contraseña del accept (400 `WEAK_PASSWORD`,
 * `details.violations[{ code, message }]`) → mensaje en español. Un código
 * fuera del catálogo cae al `message` del backend (autoridad) o a un genérico.
 */
const VIOLATION_MESSAGES: Readonly<Record<string, string>> = {
  TOO_SHORT: 'La contraseña debe tener al menos 10 caracteres.',
  MISSING_LOWERCASE: 'Debe incluir al menos una letra minúscula.',
  MISSING_UPPERCASE: 'Debe incluir al menos una letra mayúscula.',
  MISSING_DIGIT: 'Debe incluir al menos un número.',
  MISSING_SPECIAL: 'Debe incluir al menos un carácter especial.',
  BREACHED: 'Esa contraseña apareció en filtraciones conocidas. Elegí otra.',
  REUSED: 'No podés reutilizar una contraseña anterior.',
};

/**
 * Aceptación de invitación de tenant (LION-864): la pantalla PÚBLICA
 * `/aceptar-invitacion?token=…` valida el token y crea la cuenta del primer
 * admin de la empresa.
 *
 * Igual que los flujos públicos de `AuthStore` (forgot/reset), usa `fetch` a
 * propósito (bypass del árbol de interceptores: sin bearer, sin tenant header y
 * sin toast global — el 400/401/429 esperados deben llegar al formulario). El
 * token viaja auto-contenido (`<tenantId>.<64hex>` del email), por eso acá no
 * interviene `auth.tenantId` de config.
 */
@Injectable({ providedIn: 'root' })
export class TenantInvitationService {
  private readonly appConfig = inject(AppConfigService);

  /**
   * GET {apiBaseUrl}/tenant-invitations/validate?token=… (público, throttled).
   *
   * Devuelve el resultado opaco del 200. Cualquier no-200 (throttle, 5xx) o
   * fallo de red lanza `Error`: el caller decide cómo degradar (la pantalla lo
   * trata como enlace inválido genérico, sin filtrar la causa).
   */
  async validate(token: string): Promise<TenantInvitationValidation> {
    const cfg = this.appConfig.config();
    let res: Response;
    try {
      res = await fetch(
        `${cfg.apiBaseUrl}/tenant-invitations/validate?token=${encodeURIComponent(token)}`,
      );
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.');
    }

    if (!res.ok) {
      throw new Error('No se pudo validar la invitación. Intentá de nuevo en unos minutos.');
    }

    const body = (await this.readJsonSafe(res)) as { valida?: unknown } | null;
    if (body && body.valida === true) {
      const record = body as { nombreTenant?: unknown; email?: unknown };
      return {
        valida: true,
        nombreTenant: typeof record.nombreTenant === 'string' ? record.nombreTenant : '',
        email: typeof record.email === 'string' ? record.email : '',
      };
    }
    return { valida: false };
  }

  /**
   * POST {apiBaseUrl}/tenant-invitations/accept con `{ token, password }`
   * (público, throttled).
   *
   * Contrato de salida:
   * - 204 → resuelve (cuenta creada; la pantalla manda al login).
   * - 401 → {@link InvalidTenantInvitationError} (opaco: inválida/expirada/usada).
   * - 400 → {@link PasswordPolicyError} con las violaciones traducidas
   *   (`details.violations[{ code, message }]`, servidor autoritativo).
   * - 429 → {@link TooManyInvitationAttemptsError} (throttling).
   * - red / otros 5xx → Error genérico reintentable.
   */
  async accept(token: string, password: string): Promise<void> {
    const cfg = this.appConfig.config();
    let res: Response;
    try {
      res = await fetch(`${cfg.apiBaseUrl}/tenant-invitations/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
    } catch {
      throw new Error('No se pudo contactar al servidor. Revisá tu conexión e intentá de nuevo.');
    }

    if (res.ok) return;

    if (res.status === 401) {
      throw new InvalidTenantInvitationError();
    }
    if (res.status === 429) {
      throw new TooManyInvitationAttemptsError();
    }
    if (res.status === 400) {
      const violations = this.extractViolations(await this.readJsonSafe(res));
      throw new PasswordPolicyError(
        violations.length > 0
          ? violations
          : ['La contraseña no cumple los requisitos de seguridad.'],
      );
    }
    throw new Error('No se pudo crear la cuenta. Intentá de nuevo en unos minutos.');
  }

  /**
   * Violaciones de `details.violations[{ code, message }]` con prioridad al
   * código conocido (traducción al español); si no, cae al `message` del
   * backend. Nunca emite las dos cosas para el mismo ítem. Deduplicado.
   */
  private extractViolations(body: unknown): readonly string[] {
    if (!body || typeof body !== 'object') return [];
    const details = (body as { details?: unknown }).details;
    if (!details || typeof details !== 'object') return [];
    const violations = (details as { violations?: unknown }).violations;
    if (!Array.isArray(violations)) return [];

    const out: string[] = [];
    for (const violation of violations) {
      if (!violation || typeof violation !== 'object') continue;
      const { code, message } = violation as { code?: unknown; message?: unknown };
      const translated =
        typeof code === 'string' ? VIOLATION_MESSAGES[code.trim().toUpperCase()] : undefined;
      if (translated) {
        out.push(translated);
      } else if (typeof message === 'string' && message.trim()) {
        out.push(message.trim());
      }
    }
    return [...new Set(out)];
  }

  /** Body JSON tolerante: null ante body vacío o no-JSON. */
  private async readJsonSafe(res: Response): Promise<unknown> {
    try {
      return await res.json();
    } catch {
      return null;
    }
  }
}
